import { useState, useEffect } from "react";
import {
  Search, CheckCircle, XCircle, Trash2, Eye, Clock,
  ShoppingBag, Package, Users, ImageIcon, RefreshCw, AlertTriangle, CalendarClock
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { API } from "@/App";
import axios from "axios";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const statusConfig = {
  pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  approved: { label: "Activo", color: "bg-green-100 text-green-700", icon: CheckCircle },
  rejected: { label: "Rechazado", color: "bg-red-100 text-red-700", icon: XCircle },
  expired: { label: "Vencido", color: "bg-orange-100 text-orange-700", icon: AlertTriangle },
};

// Helper to format remaining days
const formatExpiration = (expiresAt) => {
  if (!expiresAt) return null;
  const now = new Date();
  const expDate = new Date(expiresAt);
  const diffMs = expDate - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return { text: "Vencido", expired: true };
  if (diffDays === 0) return { text: "Vence hoy", expired: false, warning: true };
  if (diffDays === 1) return { text: "Vence mañana", expired: false, warning: true };
  if (diffDays <= 3) return { text: `${diffDays} días`, expired: false, warning: true };
  return { text: `${diffDays} días`, expired: false };
};

export default function AdminMarketplace() {
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tab, setTab] = useState("products");

  useEffect(() => { fetchData(); }, [statusFilter, page, limit]);

  const fetchData = async () => {
    try {
      const [prodRes, sellerRes] = await Promise.all([
        axios.get(`${API}/admin/marketplace/products?status=${statusFilter}&skip=${(page - 1) * limit}&limit=${limit}`),
        axios.get(`${API}/admin/marketplace/sellers`),
      ]);
      setProducts(prodRes.data.items || []);
      setTotal(prodRes.data.total || 0);
      setSellers(sellerRes.data.items || []);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally { setLoading(false); }
  };

  const handleApprove = async (productId) => {
    try {
      await axios.put(`${API}/admin/marketplace/products/${productId}/approve`);
      toast.success("Producto aprobado");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleReject = async (productId) => {
    try {
      await axios.put(`${API}/admin/marketplace/products/${productId}/reject`);
      toast.success("Producto rechazado");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleReactivate = async (productId) => {
    try {
      await axios.put(`${API}/admin/marketplace/products/${productId}/reactivate`);
      toast.success("Producto reactivado y aprobado");
      fetchData();
    } catch { toast.error("Error al reactivar"); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API}/admin/marketplace/products/${selected.product_id}`);
      toast.success("Producto eliminado");
      setDeleteOpen(false);
      fetchData();
    } catch { toast.error("Error"); }
  };

  const filtered = products.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.seller_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingCount = products.filter(p => p.status === "pending").length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-marketplace">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text flex items-center gap-2">
              <ShoppingBag className="h-7 w-7" /> Marketplace
            </h1>
            <p className="text-brand-textSecondary mt-1">
              Gestiona productos y vendedores
              {pendingCount > 0 && <Badge className="ml-2 bg-yellow-100 text-yellow-700">{pendingCount} pendientes</Badge>}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          <Button variant={tab === "products" ? "default" : "outline"} onClick={() => setTab("products")} className="rounded-full" data-testid="tab-products">
            <Package className="h-4 w-4 mr-2" />Productos ({products.length})
          </Button>
          <Button variant={tab === "sellers" ? "default" : "outline"} onClick={() => setTab("sellers")} className="rounded-full" data-testid="tab-sellers">
            <Users className="h-4 w-4 mr-2" />Vendedores ({sellers.length})
          </Button>
        </div>

        {tab === "products" && (
          <>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Buscar producto o vendedor..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="mp-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="mp-status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="approved">Activos</SelectItem>
                  <SelectItem value="expired">Vencidos</SelectItem>
                  <SelectItem value="rejected">Rechazados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Precio</TableHead>
                    <TableHead>Categor&iacute;a</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Expira</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-400">Cargando...</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-400">No hay productos</TableCell></TableRow>
                  ) : (
                    filtered.map(p => {
                      const isExpired = p.is_expired;
                      const effectiveStatus = isExpired ? "expired" : p.status;
                      const sc = statusConfig[effectiveStatus] || statusConfig.pending;
                      const StatusIcon = sc.icon;
                      const expInfo = p.expires_at ? formatExpiration(p.expires_at) : null;
                      return (
                        <TableRow key={p.product_id} className={isExpired ? 'bg-orange-50/50' : ''} data-testid={`mp-row-${p.product_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {p.photo_urls?.[0] ? (
                                <img src={getImageUrl(p.photo_urls[0])} alt="" className="w-12 h-12 rounded-lg object-cover bg-gray-100" />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center"><ImageIcon className="h-5 w-5 text-gray-400" /></div>
                              )}
                              <div>
                                <button onClick={() => { setSelected(p); setPreviewOpen(true); }} className="font-medium text-gray-900 hover:text-primary text-left">{p.title}</button>
                                <p className="text-xs text-gray-400">{p.photo_urls?.length || 0} fotos</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium text-gray-700">{p.seller_name}</p>
                            <p className="text-xs text-gray-400">{p.seller_city}</p>
                          </TableCell>
                          <TableCell className="font-semibold text-emerald-700">L {(p.price || 0).toLocaleString("es-HN")}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{p.category}</Badge></TableCell>
                          <TableCell>
                            <Badge className={`${sc.color} text-xs`}><StatusIcon className="h-3 w-3 mr-1" />{sc.label}</Badge>
                          </TableCell>
                          <TableCell>
                            {expInfo ? (
                              <span className={`text-xs flex items-center gap-1 ${expInfo.expired ? 'text-orange-600 font-medium' : expInfo.warning ? 'text-yellow-600' : 'text-gray-400'}`}>
                                <CalendarClock className="h-3 w-3" />
                                {expInfo.text}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              {isExpired && (
                                <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-800" onClick={() => handleReactivate(p.product_id)} title="Reactivar" data-testid={`reactivate-${p.product_id}`}>
                                  <RefreshCw className="h-4 w-4" />
                                </Button>
                              )}
                              {p.status !== "approved" && !isExpired && (
                                <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-800" onClick={() => handleApprove(p.product_id)} title="Aprobar" data-testid={`approve-${p.product_id}`}>
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              )}
                              {p.status !== "rejected" && (
                                <Button variant="ghost" size="sm" className="text-yellow-600 hover:text-yellow-800" onClick={() => handleReject(p.product_id)} title="Rechazar">
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => { setSelected(p); setPreviewOpen(true); }} title="Ver">
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => { setSelected(p); setDeleteOpen(true); }}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {tab === "sellers" && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendedor</TableHead>
                  <TableHead>Correo</TableHead>
                  <TableHead>Tel&eacute;fono</TableHead>
                  <TableHead>Ciudad</TableHead>
                  <TableHead>Registro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sellers.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-gray-400">No hay vendedores</TableCell></TableRow>
                ) : (
                  sellers.map(s => (
                    <TableRow key={s.seller_id}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-sm text-gray-500">{s.email}</TableCell>
                      <TableCell className="text-sm text-gray-500">{s.phone || s.whatsapp || "—"}</TableCell>
                      <TableCell className="text-sm text-gray-500">{s.city || "—"}</TableCell>
                      <TableCell className="text-sm text-gray-400">{new Date(s.created_at).toLocaleDateString("es-HN")}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Vista previa del producto</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              {selected.photo_urls?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selected.photo_urls.map((url, i) => (
                    <img key={i} src={getImageUrl(url)} alt="" className="w-full aspect-square object-cover rounded-lg" />
                  ))}
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selected.title}</h3>
                <p className="text-2xl font-bold text-emerald-700 mt-1">L {(selected.price || 0).toLocaleString("es-HN")}</p>
              </div>
              <p className="text-gray-600 whitespace-pre-line">{selected.description}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-400">Categor&iacute;a:</span> <span className="font-medium">{selected.category}</span></div>
                <div><span className="text-gray-400">Condici&oacute;n:</span> <span className="font-medium">{selected.condition}</span></div>
                <div><span className="text-gray-400">Vendedor:</span> <span className="font-medium">{selected.seller_name}</span></div>
                <div><span className="text-gray-400">Ciudad:</span> <span className="font-medium">{selected.seller_city}</span></div>
                <div><span className="text-gray-400">WhatsApp:</span> <span className="font-medium">{selected.seller_whatsapp || "—"}</span></div>
                <div><span className="text-gray-400">Entrega:</span> <span className="font-medium">{selected.delivery_location || "—"}</span></div>
                {selected.expires_at && (
                  <div className="col-span-2">
                    <span className="text-gray-400">Expira:</span>{" "}
                    <span className={`font-medium ${selected.is_expired ? 'text-orange-600' : ''}`}>
                      {new Date(selected.expires_at).toLocaleDateString("es-HN", { day: "numeric", month: "long", year: "numeric" })}
                      {selected.is_expired && " (Vencido)"}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {selected.is_expired ? (
                  <Button onClick={() => { handleReactivate(selected.product_id); setPreviewOpen(false); }} className="bg-orange-600 hover:bg-orange-500 rounded-xl">
                    <RefreshCw className="h-4 w-4 mr-2" />Reactivar
                  </Button>
                ) : (
                  <>
                    {selected.status !== "approved" && (
                      <Button onClick={() => { handleApprove(selected.product_id); setPreviewOpen(false); }} className="bg-green-600 hover:bg-green-500 rounded-xl">
                        <CheckCircle className="h-4 w-4 mr-2" />Aprobar
                      </Button>
                    )}
                    {selected.status !== "rejected" && (
                      <Button variant="outline" onClick={() => { handleReject(selected.product_id); setPreviewOpen(false); }} className="rounded-xl">
                        <XCircle className="h-4 w-4 mr-2" />Rechazar
                      </Button>
                    )}
                  </>
                )}
                <Button variant="outline" className="text-red-500 rounded-xl ml-auto" onClick={() => { setPreviewOpen(false); setDeleteOpen(true); }}>
                  <Trash2 className="h-4 w-4 mr-2" />Eliminar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar producto</AlertDialogTitle>
            <AlertDialogDescription>&iquest;Eliminar "{selected?.title}"? Esta acci&oacute;n no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="marketplace-pagination"
          label="anuncios"
        />
    </AdminLayout>
  );
}
